import React from "react";
import PropTypes from "prop-types";
import { MessageType } from "../../../../../Constant";
import Select from "react-select";
import { Button } from "react-bootstrap";
import { fetchVehicleMaster, createMasterData, fetchCountryData } from "../../../../../services/vehicleMasterService";
import { getCallingCodeByTimezone, getCachedFlag, normalizeLengths } from "../../utils";

class OwnerCreate extends React.Component {
  constructor(props) {
    super(props);
    const activeOption = { label: "Active", value: true };
    const inactiveOption = { label: "InActive", value: false };

    let initialSelectedStatus;
    if (props.updateSelection) {
      initialSelectedStatus = props.updateSelection.status ? activeOption : inactiveOption;
    } else {
      initialSelectedStatus = activeOption;
    }

    this.state = {
      name: props.updateSelection ? props.updateSelection.name : "",
      selectedOperator: props.updateSelection
        ? (props.updateSelection.operators || []).map((o) => ({ label: o.name, value: o }))
        : [],
      phone: props.updateSelection ? props.updateSelection.phone : "",
      pendingCallingCode: props.updateSelection?.callingCode || getCallingCodeByTimezone(),
      selectedCountryCode: null,
      countryCodeOptions: [],
      countryCodeLoading: true, 
      selectedStatus: initialSelectedStatus,
      isActiveOptions: [
        { label: "Active", value: true },
        { label: "InActive", value: false },
      ],
      formError: {},
      operatorsOptions: [],
      selectedCities: [],
      cityOptions: [],
      cityOperatorMap: {},
    };
  }

  componentDidMount() {
    this.mounted = true;
    this.fetchMasterData();
    this.fetchCountryCodes();
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  handleError = (err) => {
    if (err && (err.status === 401 || err.message === "Invalid token")) {
      this.props.onComplete(MessageType.ERROR, "Invalid token");
    } else if (this.mounted) {
      this.props.initiateNotification({
        type: MessageType.ERROR,
        message: err?.description ?? "Error occurred",
      });
    }
  };

  fetchMasterData() {
    fetchVehicleMaster("city", null, 0, 1000)
      .then((res) => {
        if (this.mounted) {
          const cityOptions = res.content.map((data) => ({ label: data.name, value: data }));
          this.setState({ cityOptions });
        }
      })
      .catch((err) => this.handleError(err));
  }

  fetchCountryCodes() {
    fetchCountryData()
      .then((res) => {
        if (!this.mounted) return;

        const countryCodeOptions = (res.items || []).map((item) => {
          const flag = getCachedFlag(item.countryCode);
          return {
            label: flag ? `${flag} ${item.callingCode}` : item.callingCode,
            value: {
              countryCode: item.callingCode,
              phoneNumberPossibleLengths: normalizeLengths(item.phoneNumberPossibleLengths),
            },
          };
        });

        const { pendingCallingCode } = this.state;

        const normalize = (code) => (code ? code.replace(/^\+/, "") : "");

        let matched = countryCodeOptions.find(
          (opt) => normalize(opt.value.countryCode) === normalize(pendingCallingCode)
        );

        if (!matched) {
          matched = countryCodeOptions[0] || null;
        }

        this.setState({
          countryCodeOptions,
          selectedCountryCode: matched ? matched.value : null,
          countryCodeLoading: false,
        });
      })
      .catch((err) => {
        if (this.mounted) {
          this.setState({ countryCodeLoading: false });
          this.handleError(err);
        }
      });
  }

  handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    this.setState({ [name]: type === "checkbox" ? checked : value });
  };

  onCityChange = (value) => {
    (async () => {
      const selectedCities = value || [];
      const prevCities = this.state.selectedCities || [];

      const addedCities = selectedCities.filter(
        (c) => !prevCities.some((p) => p.value.id === c.value.id)
      );
      const removedCities = prevCities.filter(
        (c) => !selectedCities.some((s) => s.value.id === c.value.id)
      );

      let { operatorsOptions, cityOperatorMap, selectedOperator } = this.state;

      for (let city of addedCities) {
        const cityId = city.value.id;
        try {
          const res = await fetchVehicleMaster("operator", { cityId }, 0, 1000);
          const operators = res.content.map((op) => ({ label: op.name, value: op, cityId }));
          cityOperatorMap[cityId] = operators;
          operatorsOptions = [
            ...operatorsOptions,
            ...operators.filter(
              (newOp) => !operatorsOptions.some((existing) => existing.value.id === newOp.value.id)
            ),
          ];
        } catch (e) {
          this.handleError(e);
        }
      }

      for (let city of removedCities) {
        const cityId = city.value.id;
        const removedOps = cityOperatorMap[cityId] || [];
        operatorsOptions = operatorsOptions.filter((op) => op.cityId !== cityId);
        selectedOperator = selectedOperator.filter(
          (sel) => !removedOps.some((r) => r.value.id === sel.value.id)
        );
        delete cityOperatorMap[cityId];
      }

      this.setState({ selectedCities, operatorsOptions, cityOperatorMap, selectedOperator });
    })();
  };

  onOperatorChange = (value) => {
    this.setState({ selectedOperator: value });
  };

  handleSubmit = (e) => {
    e.preventDefault();
    const { name, phone, selectedOperator, selectedStatus, selectedCountryCode } = this.state;
    let formError = {};

    if (!name) formError.name = "Owner Name is required";
    if (!selectedCountryCode) formError.countryCode = "Country code is required";

    if (!phone) {
      formError.phone = "Phone number is required";
    } else if (selectedCountryCode) {
      const lengths = selectedCountryCode.phoneNumberPossibleLengths;
      const phoneLength = phone.trim().length;
      if (lengths.length > 0 && !lengths.includes(phoneLength)) {
        formError.phone =
          lengths.length === 1
            ? `Phone number must be ${lengths[0]} digits`
            : `Phone number must be ${lengths.join(" or ")} digits`;
      }
    }

    this.setState({ formError });
    if (Object.keys(formError).length > 0) return;

    const operatorIds = (selectedOperator || [])
      .map((o) => o?.value?.id)
      .filter((id) => id !== undefined && id !== null);

    const data = {
      name,
      phone,
      callingCode: selectedCountryCode ? selectedCountryCode.countryCode : undefined,
      status: selectedStatus ? selectedStatus.value : true,
      operatorIds,
    };

    if (this.props.updateSelection) {
      data.ownerCode = this.props.updateSelection.ownerCode;
    }

    this.setState({ loading: true });
    createMasterData("owner", data, this.props.updateSelection)
      .then(() => {
        this.setState({ loading: false });
        this.props.onComplete(
          MessageType.SUCCESS,
          this.props.updateSelection ? "Owner updated" : "Owner created"
        );
      })
      .catch((err) => {
        this.setState({ loading: false });
        if (err && (err.status === 401 || err.message === "Invalid token")) {
          this.props.onComplete(MessageType.ERROR, "Invalid token");
        } else {
          this.handleError(err);
          this.props.onComplete(MessageType.ERROR, err?.description ?? "Error occurred");
        }
      });
  };

  render() {
    const { onClose } = this.props;
    const {
      name,
      phone,
      formError,
      selectedOperator,
      operatorsOptions,
      loading,
      selectedCities,
      cityOptions,
      selectedStatus,
      isActiveOptions,
      selectedCountryCode,
      countryCodeOptions,
      countryCodeLoading,
    } = this.state;

    const selectedCountryOption = selectedCountryCode
      ? countryCodeOptions.find((opt) => opt.value.countryCode === selectedCountryCode.countryCode)
      : null;

    const maxPhoneLength =
      selectedCountryCode?.phoneNumberPossibleLengths?.length > 0
        ? Math.max(...selectedCountryCode.phoneNumberPossibleLengths)
        : 15;

    let submitLabel;
    if (loading) {
      submitLabel = "Saving...";
    } else if (this.props.updateSelection) {
      submitLabel = "Update";
    } else {
      submitLabel = "Create";
    }

    return (
      <div className="row">
        <form className="form-horizontal" onSubmit={this.handleSubmit}>
          <div className="col-sm-12 mb-3">
            <div className="form-group">
              <label htmlFor="ownerName" className="col-sm-3 text-start ps-2 form-label">
                <span>Owner Name</span>
              </label>
              <div className="col-sm-9">
                <input
                  id="ownerName"
                  placeholder="Enter owner name"
                  name="name"
                  value={name}
                  onChange={this.handleChange}
                  className="form-control"
                  readOnly={!!this.props.updateSelection}
                />
                {formError.name && <span className="error-text">{formError.name}</span>}
              </div>
            </div>
          </div>

          <div className="col-sm-12 mb-3">
            <div className="form-group">
              <label htmlFor="phone" className="col-sm-3 text-start ps-2 form-label">
                <span>Phone Number</span>
              </label>
              <div className="col-sm-9">
                <div className="d-flex align-items-start">
                  <div className="me-2" style={{ minWidth: 120, width: 100 }}>
                    <Select
                      inputId="countryCode"
                      value={selectedCountryOption}
                      isLoading={countryCodeLoading}
                      onChange={(value) =>
                        this.setState((prev) => ({
                          selectedCountryCode: value ? value.value : null,
                          formError: { ...prev.formError, countryCode: undefined },
                        }))
                      }
                      options={countryCodeOptions}
                      placeholder="Code"
                    />
                    {formError.countryCode && (
                      <span className="error-text">{formError.countryCode}</span>
                    )}
                  </div>
                  <div className="flex-grow-1">
                    <input
                      id="phone"
                      placeholder="Enter phone number"
                      name="phone"
                      value={phone}
                      type="tel"
                      maxLength={maxPhoneLength} // Fix #3: use resolved max length
                      onChange={this.handleChange}
                      className="form-control"
                      disabled={!selectedCountryCode || countryCodeLoading}
                    />
                    {formError.phone && <span className="error-text">{formError.phone}</span>}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-sm-12 mb-3">
            <div className="form-group">
              <label htmlFor="city" className="col-sm-3 text-start ps-2 form-label">
                <span>City</span>
              </label>
              <div className="col-sm-9">
                <Select
                  inputId="city"
                  isMulti
                  value={selectedCities}
                  onChange={this.onCityChange}
                  options={cityOptions}
                  placeholder="Select City"
                />
              </div>
            </div>
          </div>

          <div className="col-sm-12 mb-3">
            <div className="form-group">
              <label htmlFor="operator" className="col-sm-3 text-start ps-2 form-label">
                <span>Operator</span>
              </label>
              <div className="col-sm-9">
                <Select
                  inputId="operator"
                  isMulti
                  value={selectedOperator}
                  onChange={this.onOperatorChange}
                  options={operatorsOptions}
                  placeholder="Select Operator"
                />
              </div>
            </div>
          </div>

          <div className="col-sm-12 mb-3">
            <div className="form-group">
              <label htmlFor="status" className="col-sm-3 text-start ps-2 form-label">
                <span>Status</span>
              </label>
              <div className="col-sm-9">
                <Select
                  inputId="status"
                  value={selectedStatus}
                  onChange={(val) => this.setState({ selectedStatus: val })}
                  options={isActiveOptions}
                  placeholder="Select Status"
                />
              </div>
            </div>
          </div>

          <div className="col-sm-12 mb-3 form-view">
            <Button bsPrefix="close-btn" onClick={() => onClose()}>
              Close
            </Button>
            <Button
              bsPrefix="confirm-btn"
              type="submit"
              disabled={loading}
              style={{ marginLeft: 8 }}
            >
              {submitLabel}
            </Button>
          </div>
        </form>
      </div>
    );
  }
}

OwnerCreate.propTypes = {
  onComplete: PropTypes.func.isRequired,
  updateSelection: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  initiateNotification: PropTypes.func,
};

export { OwnerCreate };